import api from './apiService';
import { parseConsistentApiResponse } from '../utils/consistentApiParser';

const videoSourceService = {
  // Get all video sources
  getAll: async () => {
    if (process.env.NODE_ENV === 'development') {
      console.log('videoSourceService: Fetching all video sources...');
    }

    const response = await api.get('/video-sources');

    // Parse the consistent API response
    const apiResponse = parseConsistentApiResponse(response, 'videoSourceService', 'getAll');

    // Return the data directly (should be an array of video sources)
    return apiResponse.data || [];
  },

  // Get video source by ID
  getById: async (id) => {
    if (process.env.NODE_ENV === 'development') {
      console.log('videoSourceService: Fetching video source with ID:', id);
    }

    const response = await api.get(`/video-sources/${id}`);

    // Parse the consistent API response
    const apiResponse = parseConsistentApiResponse(response, 'videoSourceService', 'getById');

    // Return the data directly (should be a single video source object)
    return apiResponse.data;
  },

  // Create new video source
  create: async (videoSourceData) => {
    try {
      if (process.env.NODE_ENV === 'development') {
        console.log('videoSourceService: Creating video source with data:', videoSourceData);

        // Log current token for debugging
        const token = sessionStorage.getItem('token');
        if (token) {
          try {
            const tokenData = JSON.parse(atob(token.split('.')[1]));
            if (process.env.NODE_ENV === 'development') {
              console.log('videoSourceService: Current user role:', tokenData.role);
              console.log('videoSourceService: Current user info:', {
                user_id: tokenData.user_id,
                username: tokenData.username,
                role: tokenData.role,
                access_tags: tokenData.access_tags
              });
              console.log('videoSourceService: Full token payload:', tokenData);
              console.log('videoSourceService: Is user ADMIN?', tokenData.role === 'ADMIN');

              if (tokenData.role !== 'ADMIN') {
                console.warn('videoSourceService: User does not have ADMIN role - API will likely reject this request');
              }
            }
          } catch (tokenError) {
            console.error('videoSourceService: Error decoding token:', tokenError);
          }
        } else {
          console.error('videoSourceService: No token found in sessionStorage');
        }

        // Log the exact API endpoint being called
        console.log('videoSourceService: Making POST request to /video-sources');
        console.log('videoSourceService: API base URL:', api.defaults.baseURL);
      }

      const response = await api.post('/video-sources', videoSourceData);

      // Parse the consistent API response
      const apiResponse = parseConsistentApiResponse(response, 'videoSourceService', 'create');

      // Return the data directly (should be the created video source object)
      return apiResponse.data;
    } catch (error) {
      // Always log errors, even in production
      console.error('videoSourceService: Error creating video source');

      if (process.env.NODE_ENV === 'development') {
        console.error('videoSourceService: Error details:', error);
        console.error('videoSourceService: Error response data:', error.response?.data);
        console.error('videoSourceService: Error status:', error.response?.status);
        console.error('videoSourceService: Full error response:', error.response);
      }

      throw error;
    }
  },

  // Delete video source
  remove: async (id) => {
    try {
      if (process.env.NODE_ENV === 'development') {
        console.log('videoSourceService: Deleting video source with ID:', id);

        // Log current token for debugging
        const token = sessionStorage.getItem('token');
        if (token) {
          try {
            const tokenData = JSON.parse(atob(token.split('.')[1]));
            if (process.env.NODE_ENV === 'development') {
              console.log('videoSourceService: Current user role for delete:', tokenData.role);
              console.log('videoSourceService: Current user info for delete:', {
                user_id: tokenData.user_id,
                username: tokenData.username,
                role: tokenData.role,
                access_tags: tokenData.access_tags
              });
              console.log('videoSourceService: Is user ADMIN for delete?', tokenData.role === 'ADMIN');

              if (tokenData.role !== 'ADMIN') {
                console.warn('videoSourceService: User does not have ADMIN role - DELETE will likely be rejected');
              }
            }
          } catch (tokenError) {
            console.error('videoSourceService: Error decoding token for delete:', tokenError);
          }
        } else {
          console.error('videoSourceService: No token found in sessionStorage for delete');
        }
      }

      const response = await api.delete(`/video-sources/${id}`);

      // Parse the consistent API response
      const apiResponse = parseConsistentApiResponse(response, 'videoSourceService', 'remove');

      // For delete operations, return the ID if successful
      if (apiResponse.success) {
        return id;
      }

      return apiResponse;
    } catch (error) {
      // Always log errors, even in production
      console.error('videoSourceService: Error deleting video source');

      if (process.env.NODE_ENV === 'development') {
        console.error('videoSourceService: Delete error details:', error);
        console.error('videoSourceService: Delete error response data:', error.response?.data);
        console.error('videoSourceService: Delete error status:', error.response?.status);
        console.error('videoSourceService: Full delete error response:', error.response);
      }

      throw error;
    }
  }
};

export default videoSourceService;
